import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { Table as BTable, Spinner } from "react-bootstrap";
import { toast } from "react-toastify";
import { TableCell } from "@/components/Elements/TableCell";
import { managerApi } from "@/api";
import Tabs from "@/components/Elements/tabs";
import { taskTypeOptions, taskTypes } from "@/constants/taskTypes";

const columns = [
  {
    header: "Id",
    cell: (info) => <>{info.row.index}</>,
  },
  {
    accessorKey: "date",
    header: "Date",
  },
  {
    accessorKey: "time",
    header: "Time (hours)",
    cell: (info) => <>{(info.getValue() / 3600).toFixed(2)}</>,
  },
  {
    accessorKey: "money",
    header: "Money ($)",
    cell: (info) => <>{info.getValue().toFixed(2)}</>,
  },
];

const Summary = () => {
  const { user } = useSelector(({ auth }) => auth);
  const [summary, setSummary] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [taskType, setTaskType] = useState(taskTypeOptions[0].value);

  const table = useReactTable({
    data: summary[taskType] || [],
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  useEffect(() => {
    if (!user) return;

    async function fetchSummary() {
      try {
        setIsLoading(true);
        const response = await managerApi.getSummary(user.id);
        if (!response) return;
        
        if (Object.keys(response).length === 0) {
          toast.info("There is no summary data available.");
          setSummary({});
          return;
        }

        const summary = {};
        for (const taskType of taskTypes) {
          if (taskType === "all") continue;
          const flatData = Object.entries(response[taskType]);
          if (!flatData) summary[taskType] = [];
          else {
            summary[taskType] = flatData.map(([date, [money, time]]) => ({
              date,
              money,
              time,
            }));
          }
        }
        let aggregated = {};
        for (const taskType of taskTypes) {
          if (!response[taskType]) continue;
          aggregated = {
            ...aggregated,
            ...response[taskType],
          };
        }
        summary["all"] = Object.keys(aggregated).map((d) => ({
          date: d,
          money: taskTypes.reduce(
            (acc, type) => acc + (response[type]?.[d]?.[0] || 0),
            0
          ),
          time: taskTypes.reduce(
            (acc, type) => acc + (response[type]?.[d]?.[1] || 0),
            0
          ),
        }));
        setSummary(summary);
      } catch (err) {
        console.log(err);
        toast.error("Something went wrong while fetching summary.");
      } finally {
        setIsLoading(false);
      }
    }
    fetchSummary();
  }, [user]);

  return (
    <div className="tw-py-3 sm:tw-py-6 tw-h-full tw-flex tw-flex-col">
      <h2 className="text-center tw-font-medium tw-tracking-wider">Summary</h2>
      <div className="tw-w-full tw-flex tw-justify-center tw-my-3">
        <div className="tw-rounded-full tw-bg-white tw-w-fit tw-px-2 tw-py-1 tw-shadow-sm">
          <Tabs
            tabs={taskTypeOptions}
            onChange={(index) => {
              setTaskType(taskTypeOptions[index].value);
            }}
          />
        </div>
      </div>
      <div className="tw-flex tw-justify-center px-2 tw-flex-1 tw-overflow-y-auto tw-overflow-x-hidden">
        {summary[taskType] ? (
          <div className="tw-px-2 sm:tw-px-5 tw-bg-white tw-rounded-lg tw-w-full sm:tw-w-fit tw-h-full tw-shadow-sm tw-overflow-y-auto tw-overflow-x-hidden">
            <BTable className="tw-w-full sm:tw-w-[600px]" hover size="md">
              <thead className="tw-py-1 sm:tw-py-2">
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <th key={header.id} colSpan={header.colSpan}>
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody>
                {table.getRowModel().rows.map((row) => (
                  <tr key={row.id}>
                    {row.getVisibleCells().map((cell) => (
                      <TableCell key={cell.id}>
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </TableCell>
                    ))}
                  </tr>
                ))}
              </tbody>
            </BTable>
          </div>
        ) : (
          <div className="m-3 d-flex justify-content-center">
            {isLoading ? (
              <Spinner animation="border" role="status" size="sm">
                <span className="visually-hidden">Loading...</span>
              </Spinner>
            ) : (
              "There is no summary data."
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Summary;
